import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ToastContainer } from '../components/ui/ToastContainer';
import { ToastData, ToastType } from '../components/ui/Toast';

interface ToastOptions {
  message: string;
  type?: ToastType;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastContextValue {
  showToast: (options: ToastOptions) => void;
  success: (message: string, action?: ToastOptions['action']) => void;
  error: (message: string, action?: ToastOptions['action']) => void;
  info: (message: string, action?: ToastOptions['action']) => void;
  warning: (message: string, action?: ToastOptions['action']) => void;
  dismissToast: (id: string) => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

let toastIdCounter = 0;

interface ToastProviderProps {
  children: ReactNode;
  position?: 'top-center' | 'top-right' | 'bottom-center' | 'bottom-right';
  maxToasts?: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  position = 'bottom-center',
  maxToasts = 5,
}) => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const showToast = useCallback((options: ToastOptions) => {
    const id = `toast-${++toastIdCounter}`;
    const newToast: ToastData = {
      id,
      message: options.message,
      type: options.type || 'info',
      duration: options.duration || 4000,
      action: options.action,
    };

    setToasts((prev) => {
      const updated = [...prev, newToast];
      // Keep only the most recent toasts
      if (updated.length > maxToasts) {
        return updated.slice(-maxToasts);
      }
      return updated;
    });
  }, [maxToasts]);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  // Convenience methods
  const success = useCallback((message: string, action?: ToastOptions['action']) => {
    showToast({ message, type: 'success', action });
  }, [showToast]);

  const error = useCallback((message: string, action?: ToastOptions['action']) => {
    showToast({ message, type: 'error', duration: 6000, action });
  }, [showToast]);

  const info = useCallback((message: string, action?: ToastOptions['action']) => {
    showToast({ message, type: 'info', action });
  }, [showToast]);

  const warning = useCallback((message: string, action?: ToastOptions['action']) => {
    showToast({ message, type: 'warning', duration: 5000, action });
  }, [showToast]);

  const value: ToastContextValue = {
    showToast,
    success,
    error,
    info,
    warning,
    dismissToast,
    dismissAll,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer
        toasts={toasts}
        onDismiss={dismissToast}
        position={position}
      />
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastContext;
